import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../../../supabase';
import Modal from '../../../../components/Modal';
import NcrFormFields from '../NcrFormFields';
import {
  NcrFormData,
  NcrFormErrors,
  emptyNcrForm,
  validateNcr,
  withProjectSelection,
  withClientSelection,
  withContactSelection,
  buildCreateParams,
} from '../../config';

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

interface AddNcrModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNcrCreated: () => void;
  teamMembers: TeamMember[];
}

const AddNcrModal: React.FC<AddNcrModalProps> = ({
  isOpen,
  onClose,
  onNcrCreated,
  teamMembers,
}) => {
  const [formData, setFormData] = useState<NcrFormData>(emptyNcrForm());
  const [errors, setErrors] = useState<NcrFormErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(emptyNcrForm());
      setErrors({});
      setGeneralError(null);
    }
  }, [isOpen]);

  const clearError = useCallback((field: keyof NcrFormErrors) => {
    setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }, []);

  const handleChange = useCallback(<K extends keyof NcrFormData>(field: K, value: NcrFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    clearError(field as keyof NcrFormErrors);
  }, [clearError]);

  const handleProjectChange = useCallback((projectId: string | number | null, project?: any) => {
    setFormData(prev => withProjectSelection(prev, projectId, project));
  }, []);

  const handleClientChange = useCallback((clientId: string | number | null) => {
    setErrors(prev => ({ ...prev, client_id: undefined, contact_id: undefined }));
    setFormData(prev => withClientSelection(prev, clientId));
  }, []);

  const handleContactChange = useCallback((contactId: string | number | null) => {
    clearError('contact_id');
    setFormData(prev => withContactSelection(prev, contactId));
  }, [clearError]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = validateNcr(formData);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setGeneralError('Please complete the required fields before creating the NCR.');
      return;
    }

    setIsSubmitting(true);
    setGeneralError(null);

    try {
      const { data, error } = await supabase.rpc('create_non_conformance', buildCreateParams(formData));

      if (error) throw error;
      if (data && data.success === false) {
        throw new Error(data.error || 'Failed to create non-conformance');
      }

      onNcrCreated();
      onClose();
    } catch (err) {
      console.error('Error creating non-conformance:', err);
      setGeneralError(err instanceof Error ? err.message : 'An error occurred while creating the NCR');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onNcrCreated, onClose]);

  const modalFooter = (
    <div className="modal-footer-actions">
      <button type="button" onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button type="submit" form="add-ncr-form" disabled={isSubmitting} className="btn-primary">
        {isSubmitting ? 'Creating...' : 'Create NCR'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Raise Non-Conformance"
      size="md"
      className="ncr-modal"
      hasFooter={true}
      footerContent={modalFooter}
    >
      <div className="form-content">
        <form id="add-ncr-form" onSubmit={handleSubmit}>
          {generalError && <div className="error-message general-error">{generalError}</div>}
          <NcrFormFields
            formData={formData}
            onChange={handleChange}
            onProjectChange={handleProjectChange}
            onClientChange={handleClientChange}
            onContactChange={handleContactChange}
            teamMembers={teamMembers}
            errors={errors}
            required
          />
        </form>
      </div>
    </Modal>
  );
};

export default AddNcrModal;
